import L from "leaflet";
import { useMemo } from "react";
import { Marker, Polyline, Popup } from "react-leaflet";
import type { RouteCoordinate } from "../types";
import { calculateBearing } from "../utils/tracking.utils";
import { RoutePointCard } from "./cards";

const NORMAL_ROUTE_COLOR = "#2E90FA";
const OVER_SPEED_COLOR = "#F04438";
const OVER_SPEED_LIMIT_KMH = 110;
const MAX_DIRECTION_MARKERS = 150;

const createArrowIcon = (bearing: number, color: string) =>
	L.divIcon({
		html: `<div style="transform: rotate(${bearing}deg); width: 16px; height: 16px; cursor: pointer;">
			<svg height="16" viewBox="0 0 24 24" width="16" xmlns="http://www.w3.org/2000/svg">
				<path d="M12 2 L20 20 L12 15.5 L4 20 Z" fill="${color}" stroke="#ffffff" stroke-width="1.5" />
			</svg>
		</div>`,
		className: "route-direction-arrow",
		iconAnchor: [8, 8],
		iconSize: [16, 16],
	});

type RouteSegment = {
	bearing: number;
	color: string;
	dateTelemetry: string;
	key: string;
	position: [number, number];
	positions: [[number, number], [number, number]];
	speed: number;
};

type PolylineRun = {
	color: string;
	key: string;
	positions: [number, number][];
};

const buildSegments = (points: RouteCoordinate[]): RouteSegment[] =>
	points.slice(0, -1).map((point, index) => {
		const nextPoint = points[index + 1];
		const speed = point.speed ?? 0;
		const isOverSpeed = speed > OVER_SPEED_LIMIT_KMH;
		const color = isOverSpeed ? OVER_SPEED_COLOR : NORMAL_ROUTE_COLOR;

		return {
			bearing: calculateBearing(point, nextPoint),
			color,
			dateTelemetry: point.dateTelemetry ?? "",
			key: `${point.lat}-${point.lng}-${index}`,
			position: [point.lat, point.lng],
			positions: [
				[point.lat, point.lng],
				[nextPoint.lat, nextPoint.lng],
			],
			speed,
		};
	});

const buildMarkerSegments = (segments: RouteSegment[]): RouteSegment[] => {
	const markerStep = Math.max(
		1,
		Math.ceil(segments.length / MAX_DIRECTION_MARKERS)
	);
	return segments.filter(
		(_, index) => index % markerStep === 0 || index === segments.length - 1
	);
};

const buildPolylineRuns = (segments: RouteSegment[]): PolylineRun[] => {
	const runs: PolylineRun[] = [];

	for (const segment of segments) {
		const lastRun = runs.at(-1);
		if (lastRun && lastRun.color === segment.color) {
			lastRun.positions.push(segment.positions[1]);
			continue;
		}

		runs.push({
			color: segment.color,
			key: `run-${segment.key}`,
			positions: [segment.positions[0], segment.positions[1]],
		});
	}

	return runs;
};

type RouteWithDirectionProps = {
	points: RouteCoordinate[];
};

export const RouteWithDirection = ({ points }: RouteWithDirectionProps) => {
	const segments = useMemo(() => buildSegments(points), [points]);
	const polylineRuns = useMemo(() => buildPolylineRuns(segments), [segments]);
	const markerSegments = useMemo(
		() => buildMarkerSegments(segments),
		[segments]
	);

	if (points.length < 2) {
		return null;
	}

	return (
		<>
			{polylineRuns.map((run) => (
				<Polyline
					color={run.color}
					key={run.key}
					lineJoin="round"
					opacity={0.8}
					positions={run.positions}
					weight={4}
				/>
			))}
			{markerSegments.map((segment) => (
				<Marker
					icon={createArrowIcon(segment.bearing, segment.color)}
					key={`arrow-${segment.key}`}
					position={segment.position}
				>
					<Popup className="custom-popup" maxHeight={180}>
						<RoutePointCard
							data={{
								latitude: String(segment.position[0]),
								longitude: String(segment.position[1]),
								speed: segment.speed,
								dateTelemetry: segment.dateTelemetry,
							}}
							style={{ padding: 0, background: "none", border: "none" }}
						/>
					</Popup>
				</Marker>
			))}
		</>
	);
};
